import { Link, useLocation } from "react-router-dom";
import ReaderGuidePage from "./ReaderGuidePage";
import { readerGuideByPath } from "../data/readerGuides20261006";
import SEO from "../components/SEO";
import PageHeader from "../components/PageHeader";
import Breadcrumbs, { buildBreadcrumbSchema } from "../components/Breadcrumbs";
import CTASection from "../components/CTASection";
import { SectionIntro, ResearchTable, DeepCard, BulletGrid, LinkPills } from "../components/ContentBlocks";
import SEOResearchBlock from "../components/SEOResearchBlock";
import { roboticsTopics } from "../data/roboticsTopics";
import { robotCategories } from "../data/robotCategories";

const groups = Array.from(new Set(roboticsTopics.map((topic) => topic.group)));
const categoryBySlug = Object.fromEntries(robotCategories.map((category) => [category.slug, category]));
const practicalRobotRows = [
  ["Material handling", "Move totes, trays, cartons, pallets or bins between fixed points", "payload, route stability, charging, safety scanners and WMS integration"],
  ["Robotic parts handling", "Pick, orient, place or feed parts into a process", "gripper geometry, vision lighting, part variation, cycle time and jam recovery"],
  ["Inspection", "Collect images, measurements, thermal data or radiation readings", "sensor calibration, access, lighting, reporting and human validation"],
  ["Manipulation", "Open, pull, press, carry, insert or assemble objects", "hands, force control, tactile feedback, fixtures and failure recovery"],
  ["Field work", "Weed, harvest, map, sample, patrol or survey outside a factory", "weather, dirt, terrain, GPS loss, maintenance and supervision"]
];



const safetyRows = [
  ["AI safety", "Keep model output behind safety checks, speed limits, force limits, safe stop logic and human override", "humanoids, mobile manipulators, AMRs and field robots near people"],
  ["Research topics", "Study perception, SLAM, manipulation, tactile sensing, control, locomotion, simulation, HRI and robot learning", "students, founders and technical teams choosing a robotics field"],
  ["Robotics fields", "Separate mechanical design, embedded electronics, software, sensing, controls, operations and maintenance", "beginners who need a clean map of the domain"],
  ["Robot control", "Relate motion commands to sensor feedback, stability and recovery", "feedback loops, stability, safety layers and recovery behavior"]
];

const robotAbilityCards = [
  {
    title: "What can the robot do",
    description: "What can the robot do is the right question only after the task is defined. A robot may navigate a mapped warehouse, inspect a pipe, pick a known part or weld a fixture, yet fail when lighting, object pose, floor friction or human traffic changes.",
    points: ["define the task", "measure intervention rate", "separate demo from routine work"]
  },
  {
    title: "Robotic parts handling",
    description: "Robotic parts handling depends on boring details part geometry, surface finish, bin depth, fixture design, lighting, gripper contact, force limits, cycle time and what happens when two parts stick together.",
    points: ["part presentation", "gripper and vision setup", "jam detection and recovery"]
  }
];

const additionalGuideLinks = [
  { label: "Complete robots guide", to: "/robots", description: "Robot types, hardware, sensors and software in one index." },
  { label: "Robotics editorial map", to: "/robotics-search-topics", description: "Search intent and technical topic groups for robotics research." },
  { label: "Robotic arm joint components", to: "/robotic-arm-joint-components", description: "Motors, reducers, encoders, bearings, brakes and control loops." },
  { label: "Nuclear robots", to: "/nuclear-robots", description: "Inspection, remote handling and decommissioning systems." },
  { label: "Construction robotics", to: "/construction-robotics", description: "Robots for building sites, inspection and automated construction." },
  { label: "Robotics conferences", to: "/robotics-conferences", description: "Research conferences, industry meetings and technical events." },
  { label: "Entertainment robotics", to: "/entertainment-robotics", description: "Animatronics, social robots and interactive entertainment systems." },
  { label: "Micro robotics", to: "/micro-robotics", description: "Small-scale robot mechanisms, sensing and research applications." },
  { label: "Drones and robotics", to: "/drones-plus-robotics", description: "Aerial robots, autonomy, sensors and field deployment." }
];


export default function RoboticsTopics() {
  const location = useLocation();
  if (location.pathname.replace(/\/$/, "") === "/robotics") return <ReaderGuidePage guide={readerGuideByPath["/robotics"]} />;
  const isGuidesHub = location.pathname === "/robotics";
  const pagePath = isGuidesHub ? "/robotics" : "/robotics-topics";
  const pageLabel = isGuidesHub ? "Robotics Guides" : "Robotics Topics";
  const pageTitle = isGuidesHub
    ? "Robotics Guides Hardware, Software and Physical AI Topics"
    : "Robotics Topics Research Fields, Systems and Technical Guide";
  const pageDescription = isGuidesHub
    ? "Robotics guides covering hardware, sensors, software, Physical AI, manipulation, navigation, safety and real deployment limits."
    : "Robotics topics covering SLAM, perception, control, manipulation, Physical AI, ROS 2, sensors, actuators, safety and humanoids.";
  const breadcrumbs = [
    { label: "Home", to: "/" },
    { label: pageLabel, to: pagePath }
  ];

  return (
    <>
      <SEO
        title={pageTitle}
        description={pageDescription}
        path={pagePath}
        type="article"
        schema={buildBreadcrumbSchema(breadcrumbs)}
      />
      <Breadcrumbs items={breadcrumbs} />
      <PageHeader
        eyebrow={isGuidesHub ? "TechniaHQRobot robotics guides" : "Robotics research map"}
        title={isGuidesHub ? "Robotics Guides" : "Robotics Topics"}
        description={isGuidesHub
          ? "A structured library for robot hardware, sensing, software, Physical AI, control, safety and deployment."
          : "A clear map of the concepts behind humanoids, robot dogs, drones, industrial robots, medical robots, autonomous vehicles, AMRs, cobots, soft robotics, swarm robotics and Physical AI."}
      />

      <section className="site-shell py-10 sm:py-14">
        <div className="soft-card bg-neutral-50/90 p-6 sm:p-8">
          <h2 className="text-2xl font-bold tracking-tight text-neutral-950">How to read robotics seriously</h2>
          <p className="mt-4 max-w-5xl text-base leading-8 text-neutral-700">
            A viral robot video usually shows the outside of the machine. The real robotics story is inside the stack sensors, calibration, state estimation, planning, control, contact, power, software architecture, safety, maintenance, data and deployment economics. TechniaHQRobot’s topic map connects short robot clips to the technical systems behind them.
          </p>
          <div className="mt-5 flex flex-wrap gap-2">
            <Link to="/robot-sensors" className="rounded-full bg-blue-600 px-4 py-2 text-sm font-bold text-white">Open robot sensors guide</Link>
            <Link to="/robot-types" className="rounded-full border border-neutral-200 bg-white px-4 py-2 text-sm font-bold text-neutral-950">Open robot types index</Link>
          </div>
        </div>
      </section>

      {isGuidesHub && (
        <section className="site-shell py-10 sm:py-14" aria-labelledby="additional-robotics-guides">
          <div className="soft-card p-6 sm:p-8">
            <h2 id="additional-robotics-guides" className="text-2xl font-black tracking-tight text-neutral-950">Additional robotics guides</h2>
            <p className="mt-3 max-w-4xl text-base leading-8 text-neutral-600">Open focused guides for robot hardware, field systems, conferences and specialist deployment environments.</p>
            <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {additionalGuideLinks.map((item) => (
                <article key={item.to} className="rounded-2xl border border-neutral-200 bg-neutral-50 p-4">
                  <h3 className="font-black text-neutral-950"><Link to={item.to} className="hover:text-blue-700">{item.label}</Link></h3>
                  <p className="mt-2 text-sm leading-6 text-neutral-600">{item.description}</p>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="site-shell py-12 sm:py-16">
        <SectionIntro
          eyebrow="Robot use cases"
          title="What robots can do today depends on the task and environment"
          description="A useful robotics topic page should answer practical use cases before abstract hype. The same robot can look impressive in a controlled demo and still need fixtures, maps, lighting, remote assistance or operator recovery in production."
          action={<LinkPills items={[{ label: "Robot components", to: "/robot-components" }, { label: "Robotics software", to: "/robotics-software" }, { label: "Robot management", to: "/robot-management-software" }, { label: "Robot types", to: "/robot-types" }]} />}
        />
        <ResearchTable columns={["Use case", "What the robot does", "What decides reliability"]} rows={practicalRobotRows} />
        <div className="mt-6 grid gap-4 md:grid-cols-2">
          {robotAbilityCards.map((card) => (
            <DeepCard key={card.title} title={card.title} description={card.description}>
              <BulletGrid title="Technical checks" items={card.points} />
            </DeepCard>
          ))}
        </div>
      </section>


      <section className="site-shell py-12 sm:py-16">
        <SectionIntro
          eyebrow="Robotics topic guide"
          title="Where safety and control fit in a robot system"
          description="Separate model behavior, feedback control and safety functions when evaluating an application. The table connects each question to the systems involved."
          action={<LinkPills items={[{ label: "Robotics software", to: "/robotics-software" }, { label: "Robot sensors", to: "/robot-sensors" }, { label: "Robot components", to: "/robot-components" }]} />}
        />
        <ResearchTable columns={["Technical question", "What to cover", "Relevant robots"]} rows={safetyRows} />
      </section>

      <section className="mx-auto grid max-w-7xl gap-12 px-4 py-8 sm:px-6 lg:px-8">
        {groups.map((group) => {
          const items = roboticsTopics.filter((topic) => topic.group === group);
          return (
            <section key={group}>
              <div className="mb-5 border-b border-neutral-200 pb-4">
                <p className="text-xs font-bold uppercase tracking-[0.22em] text-neutral-500">{items.length} subjects</p>
                <h2 className="mt-2 text-2xl font-bold tracking-tight text-neutral-950">{group}</h2>
              </div>
              <div className="grid gap-4 lg:grid-cols-2">
                {items.map((topic) => (
                  <article key={topic.id} className="soft-card p-5">
                    <h3 className="text-xl font-bold text-neutral-950">{topic.title}</h3>
                    <p className="mt-3 text-sm leading-6 text-neutral-600">{topic.definition}</p>
                    <div className="mt-4 flex flex-wrap gap-2">
                      {topic.coreIdeas.map((idea) => (
                        <span key={idea} className="data-badge">{idea}</span>
                      ))}
                    </div>
                    <div className="mt-4">
                      <h4 className="text-sm font-bold text-neutral-950">Related robot types</h4>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {topic.relatedRobotTypes.slice(0, 4).map((slug) => {
                          const category = categoryBySlug[slug];
                          return category ? (
                            <Link key={slug} to={`/robot-types/${slug}`} className="rounded-full border border-neutral-200 px-3 py-1 text-xs font-bold hover:border-blue-200">
                              {category.name}
                            </Link>
                          ) : null;
                        })}
                      </div>
                    </div>

                  </article>
                ))}
              </div>
            </section>
          );
        })}
      </section>
      <section className="site-shell py-10">
        <BulletGrid title="Questions for evaluating a robot system" items={[
          "Which task and operating conditions were tested?",
          "How does the system handle uncertain measurements or sensor disagreement?",
          "Which failures trigger a safe stop or human intervention?",
          "What maintenance and operator workload remain after deployment?"
        ]} />
      </section>
      <SEOResearchBlock />
      <CTASection title="Follow TechniaHQRobot for the full robotics research map" />
    </>
  );
}
